import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Checkbox, Grid, TableCell, TableRow, Tooltip } from '@mui/material';
import { useMutation, useSubscription } from 'hooks/apollo';
import { Typography, IconButton } from '@totalsoft/rocket-ui';
import { DELETE_COMMISSION_CALCULATION_METHOD } from '../mutations';
import { COMMISSION_CALCULATION_METHOD_DELETED } from '../subscriptions';
import { methodModalVar } from '../variables';
import { TableCellWithOverflow } from 'components/list/TableComponentsStyled';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { commissionCalculationMethodsStore } from './ListBrowserComponent';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_COMMISSION_CALCULATION_METHOD_WRITE } from 'constants/rightsCodes';

const ItemComponent = ({ data }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const confirm = useConfirmDialog();
  const { store } = useListBrowserUtils(commissionCalculationMethodsStore);
  const canWrite = usePermission(ADMIN_COMMISSION_CALCULATION_METHOD_WRITE);

  const [deleting, setDeleting] = useState(false);

  // #region Delete

  const [deleteMethod] = useMutation(DELETE_COMMISSION_CALCULATION_METHOD, {
    onError: () => {
      setDeleting(false);
    }
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        setDeleting(true);
        deleteMethod({ variables: { id: data.id } });
      },
      {
        title: t('CommissionCalculationMethod.ConfirmDelete')
      }
    );
  }, [t, data, deleteMethod, confirm]);

  useSubscription(COMMISSION_CALCULATION_METHOD_DELETED, {
    variables: { methodId: data.id },
    skip: !data.id,
    onData: _ => {
      addToast(t('CommissionCalculationMethod.Deleted'), 'success');
      store.refetch();
    }
  });

  // #endregion

  return (
    <TableRow>
      <Tooltip title={data.name}>
        <TableCellWithOverflow>{data.name}</TableCellWithOverflow>
      </Tooltip>
      <TableCell>
        <Typography title={data.code}>{data.code}</Typography>
      </TableCell>
      <TableCell>
        <Typography title={data.typeName}>{data.typeName}</Typography>
      </TableCell>
      <TableCell align='center'>
        <Checkbox checked={data.percentIsMandatory} disabled />
      </TableCell>
      <TableCell align='center'>
        <Checkbox checked={data.assetIsMandatory} disabled />
      </TableCell>
      <TableCell align='center'>
        <Checkbox checked={data.ppIsMandatory} disabled />
      </TableCell>
      <Tooltip title={data.amountFormula}>
        <TableCellWithOverflow>{data.amountFormula}</TableCellWithOverflow>
      </Tooltip>
      <Tooltip title={data.percentFormula}>
        <TableCellWithOverflow>{data.percentFormula}</TableCellWithOverflow>
      </Tooltip>
      <TableCell>
        <Grid container wrap='nowrap' justifyContent='end' gap={0.5}>
          <IconButton
            type='edit'
            size='small'
            color='transparent'
            disabled={deleting}
            title={t('Edit')}
            onClick={() => methodModalVar({ open: true, method: data })}
          />
          <IconButton
            type='delete'
            size='small'
            color='transparent'
            title={t('Delete')}
            disabled={deleting || !canWrite}
            onClick={handleDelete}
          />
        </Grid>
      </TableCell>
    </TableRow>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object
};

export default ItemComponent;
